import { useState } from "react";
import { Link, useParams } from "@tanstack/react-router";
import { Settings, LogOut, ChevronUp, Globe } from "lucide-react";
import * as Popover from "@radix-ui/react-popover";
import { useMutation } from "convex/react";
import { api } from "convex/_generated/api";
import { Avatar } from "@/components/ui";
import { useSignOut } from "@/packages/features-auth";
import {
  useTranslation,
  changeLanguage,
  getCurrentLanguage,
} from "@/packages/utils";
import { useSidebar } from "./SidebarContext";
import { SidebarTooltip } from "./Tooltip";
import type { CurrentUserProps } from "./types";

const I18N_TO_AI_LANGUAGE: Record<string, string> = {
  en: "English",
  de: "German",
};

interface SidebarUserMenuProps {
  user: CurrentUserProps;
}

function MenuItem({
  icon: Icon,
  label,
  to,
  destructive,
  onClick,
}: {
  icon: typeof Settings;
  label: string;
  to?: string;
  destructive?: boolean;
  onClick?: () => void;
}) {
  const base =
    "flex items-center gap-2.5 px-3 py-[6px] rounded-md text-[13px] w-full transition-colors cursor-pointer";
  const colorClass = destructive
    ? "text-red-500 hover:bg-red-500/10"
    : "text-secondary hover:bg-card-hover hover:text-primary";

  if (to) {
    return (
      <Popover.Close asChild>
        <Link
          to={to}
          params={(prev) => prev}
          search={(prev) => prev}
          className={`${base} ${colorClass}`}
        >
          <Icon size={15} strokeWidth={1.75} className="shrink-0" />
          <span>{label}</span>
        </Link>
      </Popover.Close>
    );
  }

  return (
    <Popover.Close asChild>
      <button
        type="button"
        onClick={onClick}
        className={`${base} ${colorClass}`}
      >
        <Icon size={15} strokeWidth={1.75} className="shrink-0" />
        <span>{label}</span>
      </button>
    </Popover.Close>
  );
}

export function SidebarUserMenu({ user }: SidebarUserMenuProps) {
  const { collapsed } = useSidebar();
  const { signOut } = useSignOut();
  const { t } = useTranslation(["sidebar"]);
  const { workspaceSlug } = useParams({ strict: false });
  const [open, setOpen] = useState(false);
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const updateAiLanguage = useMutation(
    api.workspace_ai_preferences_mutations.updatePreferredLanguage,
  );

  const handleLanguageChange = async (lang: string) => {
    await changeLanguage(lang);
    setCurrentLang(lang);

    const aiLanguage = I18N_TO_AI_LANGUAGE[lang];
    if (aiLanguage && user.workspaceId) {
      updateAiLanguage({
        workspaceId: user.workspaceId,
        preferredLanguage: aiLanguage,
      });
    }

    window.location.reload();
  };

  return (
    <Popover.Root open={open} onOpenChange={setOpen}>
      <SidebarTooltip label={user.name} enabled={collapsed && !open}>
        <Popover.Trigger asChild>
          <button
            type="button"
            className={`flex items-center w-full rounded-md transition-colors hover:bg-card-hover cursor-pointer ${
              collapsed ? "justify-center px-0 py-2" : "gap-2.5 px-2.5 py-2"
            }`}
          >
            <Avatar
              name={user.name}
              avatarUrl={user.avatarUrl}
              avatarColor={user.avatarColor}
              size="sm"
            />
            {!collapsed && (
              <>
                <span className="flex-1 text-left text-[13px] font-medium text-primary truncate">
                  {user.name}
                </span>
                <ChevronUp
                  size={14}
                  className={`shrink-0 text-muted transition-transform ${open ? "" : "rotate-180"}`}
                />
              </>
            )}
          </button>
        </Popover.Trigger>
      </SidebarTooltip>

      <Popover.Portal>
        <Popover.Content
          side={collapsed ? "right" : "top"}
          align="start"
          sideOffset={6}
          className="z-50 bg-card rounded-card border border-border shadow-lg p-1 min-w-[220px] animate-in fade-in-0 zoom-in-95"
        >
          {/* User info block */}
          <div className="flex items-center gap-3 px-3 py-2.5">
            <Avatar
              name={user.name}
              avatarUrl={user.avatarUrl}
              avatarColor={user.avatarColor}
              size="md"
            />
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-primary truncate">
                {user.name}
              </p>
              <p className="text-[11px] text-muted truncate">{user.email}</p>
            </div>
          </div>

          <div className="mx-1 h-px bg-border my-1" />

          {/* Menu items */}
          <MenuItem
            icon={Settings}
            label={t("sidebar:userMenu.settings")}
            to={workspaceSlug ? `/${workspaceSlug}/settings` : "/settings"}
          />
          {/* Language selector */}
          <div className="flex items-center gap-2.5 px-3 py-[6px] rounded-md text-[13px] text-secondary hover:bg-card-hover hover:text-primary transition-colors cursor-pointer">
            <Globe size={15} strokeWidth={1.75} className="shrink-0" />
            <div className="flex gap-1">
              <button
                type="button"
                onClick={() => handleLanguageChange("en")}
                className={`px-1.5 py-0.5 rounded transition-colors ${
                  currentLang === "en"
                    ? "bg-primary text-card font-medium"
                    : "hover:bg-card-hover"
                }`}
              >
                {t("sidebar:userMenu.langEn")}
              </button>
              <button
                type="button"
                onClick={() => handleLanguageChange("de")}
                className={`px-1.5 py-0.5 rounded transition-colors ${
                  currentLang === "de"
                    ? "bg-primary text-card font-medium"
                    : "hover:bg-card-hover"
                }`}
              >
                {t("sidebar:userMenu.langDe")}
              </button>
            </div>
          </div>

          <div className="mx-1 h-px bg-border my-1" />

          {/* Log out */}
          <MenuItem
            icon={LogOut}
            label={t("sidebar:userMenu.logOut")}
            destructive
            onClick={signOut}
          />
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  );
}
